import { getCommonPinningStyles } from '@repo/lib';
import { Cell, type ColumnDef, flexRender } from '@tanstack/react-table';
import { TableCell } from '@ui/components';
import { cn } from '@ui/lib/utils';
import { ChevronDownIcon, ChevronRightIcon } from 'lucide-react';
import { CSSProperties, HTMLProps, useCallback, useMemo } from 'react';

interface DataTableDragCellProps<TData, TValue> {
  cell: Cell<TData, TValue>;
  styleCell?: CSSProperties;
  classNameCell?: HTMLProps<HTMLElement>['className'];
  onRowClick?: (data: TData, column: ColumnDef<TData, unknown>) => void;
  onDoubleClick?: (data: TData, column: ColumnDef<TData, unknown>) => void;
}

/**
 * @deprecated This component is deprecated and will be removed in a future version.
 * Please use AppTable component instead.
 * @see src/components/app-table
 */

const DataTableDragCellComponent = <TData, TValue>(
  props: DataTableDragCellProps<TData, TValue>,
) => {
  const { cell, classNameCell, onRowClick, onDoubleClick, styleCell } = props;

  const handleRowClick = useCallback(() => {
    onRowClick?.(
      cell.row?.original,
      cell.column.columnDef as ColumnDef<TData, unknown>,
    );
  }, [cell.row?.original, cell.column.columnDef, onRowClick]);

  const handleDoubleClick = useCallback(() => {
    onDoubleClick?.(
      cell.row?.original,
      cell.column.columnDef as ColumnDef<TData, unknown>,
    );
  }, [cell.row?.original, cell.column.columnDef, onDoubleClick]);

  const isGrouped = useMemo(() => cell.getIsGrouped(), [cell.getIsGrouped]);

  return (
    <TableCell
      key={cell.id}
      className={cn(
        'overflow-hidden text-ellipsis whitespace-nowrap [&:not(:last-child)]:border-r',
        classNameCell,
      )}
      style={{
        ...styleCell,
        ...getCommonPinningStyles({
          column: cell.column,
          isSelected: cell.row.getIsSelected(),
        }),
      }}
      onClick={handleRowClick}
      onDoubleClick={handleDoubleClick}
    >
      {isGrouped ? (
        <>
          <button
            className="flex flex-row items-center gap-2"
            onClick={cell.row.getToggleExpandedHandler()}
          >
            {cell.row.getIsExpanded() ? (
              <ChevronDownIcon className="size-4" />
            ) : (
              <ChevronRightIcon className="size-4" />
            )}{' '}
            {flexRender(cell.column.columnDef.cell, cell.getContext())} (
            {cell.row.subRows.length})
          </button>
        </>
      ) : cell.getIsPlaceholder() ? null : (
        flexRender(cell.column.columnDef.cell, cell.getContext())
      )}
    </TableCell>
  );
};

export const DataTableDragCell = DataTableDragCellComponent;
